import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { ChevronUp, LogOut, User } from "lucide-react";
import { Avatar, AvatarGradientFallback, Mono, Text } from "@athena/ui";
import { cn } from "@athena/ui/utils";
import type { Role } from "@athena/common/graphql/generated/schema";
import { getInitialsFrom } from "../../../global/formatHelpers";
import { roleLabelMap } from "@/types/Roles";

/**
 * The account control in the sidebar footer: one surface that grows to hold its own actions.
 *
 * **Why this is not a `DropdownMenu`.** It was, and a dropdown renders through a portal — a
 * separate floating card, positioned over the trigger rather than joined to it. That is why it
 * read as two disconnected things: its own elevation, its own `bg-sidebar` against the trigger's
 * `bg-card`, and a hard edge between them. No amount of styling fixes that, because a portalled
 * element cannot be part of the trigger's box.
 *
 * So this is a disclosure instead. The actions and the account row live in the *same* bordered
 * element, and opening grows that one element upward from the sidebar's bottom edge: the height
 * animates, the chevron turns over, and the corners stay exactly as round as they were. Nothing
 * appears beside it or on top of it.
 *
 * Being a disclosure rather than a menu means the behavior a dropdown gave for free is written
 * out below — Escape, click-away, and keeping collapsed actions out of the tab order.
 */

interface SidebarUserMenuProps {
    user?: { firstName?: string | null; lastName?: string | null; email: string; role: Role } | null;
    loading: boolean;
    /** Where "Profile" goes. The alumni console has its own, since the portal's is outside it. */
    profilePath?: string;
}

export function SidebarUserMenu({ user, loading, profilePath = "/profile" }: SidebarUserMenuProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { logout } = useAuth0();
    const [
        open,
        setOpen,
    ] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const actionsId = useId();

    /*
     * The panel's natural height, measured, because `max-height` is what actually animates.
     *
     * The obvious version of this is a grid row eased from `0fr` to `1fr`, which needs no
     * measuring. It does not animate: with the transition declared and the classes applied,
     * `grid-template-rows` fires no `transitionrun` at all and the height snaps from 50px to
     * 133px in a single frame. `max-height` interpolates everywhere, and the cost is knowing the
     * number to interpolate to.
     *
     * Measured through a `ResizeObserver` rather than once on mount, so a wrapped label, a
     * translation longer than the English, or a third action added later all keep working — a
     * hard-coded height would silently clip them.
     */
    const [
        panelHeight,
        setPanelHeight,
    ] = useState(0);

    /*
     * The corner radius, which stays put rather than changing with the state.
     *
     * `rounded-full` is the collapsed pill, and a full radius means *half the height* — so the
     * moment the surface grows, the same class turns it into a lozenge. Easing it to `rounded-lg`
     * instead was worse in a subtler way: the corners visibly unwound as the panel opened, and 8px
     * is not the curvature the collapsed pill has.
     *
     * So the radius is pinned to half the *collapsed* surface's height, which is the pill radius
     * the closed state has always had — 25px on a 50px box, borders included. Closed is therefore
     * pixel-identical to before; open gets those same corners on a taller box.
     *
     * Measured rather than written as 25px because that height comes from the row's padding, its
     * avatar size and the border width, and hard-coding it would drift the first time any of them
     * changes. Only measured while closed: once the surface has grown, half its height is the
     * lozenge this is avoiding.
     */
    const [
        cornerRadius,
        setCornerRadius,
    ] = useState(0);

    // Read through a ref so the observer below is installed once rather than torn down and
    // rebuilt on every toggle.
    const openRef = useRef(open);
    openRef.current = open;

    useEffect(() => {
        const content = contentRef.current;
        const container = containerRef.current;
        if (!content || !container) return;

        const measure = () => {
            setPanelHeight(content.scrollHeight);
            if (!openRef.current) setCornerRadius(container.offsetHeight / 2);
        };
        measure();

        // Absent in jsdom. Without it both numbers keep the values measured on mount, which is
        // right until the content changes — a degraded panel rather than a broken one.
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(content);
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    /*
     * Escape and click-away, which a menu would have handled.
     *
     * `pointerdown` rather than `click`: a click that starts inside the panel and ends outside it
     * would otherwise close the panel before the button's own handler ran, so choosing "Sign out"
     * with a slightly dragged mouse did nothing. Escape returns focus to the trigger, or it lands
     * on `<body>` and the keyboard path is lost.
     */
    useEffect(() => {
        if (!open) return;

        const onPointerDown = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            setOpen(false);
            triggerRef.current?.focus();
        };

        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const closeAndGo = (action: () => void) => {
        setOpen(false);
        action();
    };

    /** Shared by both actions: full-width row, muted until hovered, icon then label. */
    const actionClass =
        "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground " +
        "focus-visible:bg-sidebar-accent focus-visible:text-sidebar-foreground focus-visible:outline-none " +
        "flex w-full cursor-pointer items-center rounded-md px-3 py-2 text-sm transition-colors " +
        "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0";

    return (
        <div
            ref={containerRef}
            data-state={open ? "open" : "closed"}
            style={{ borderRadius: cornerRadius || undefined }}
            className={cn(
                "border-border bg-card overflow-hidden border",
                // The fallback while unmeasured — the collapsed pill, which is what it is at that
                // point anyway. The inline radius above takes over once the row has been measured.
                "rounded-full",
                // The icon rail has no room for a bordered card, and never had one.
                "group-data-[collapsible=icon]:border-0",
            )}
        >
            {/*
             * The growth itself: `max-height` eased to the measured height above.
             *
             * `overflow-hidden` is what hides the actions while the panel is collapsed rather
             * than letting them spill out of it. An unmeasured height (jsdom, or the first frame
             * before the observer has run) falls back to no cap, so the layout is right even when
             * the animation cannot be.
             */}
            <div
                className="overflow-hidden transition-[max-height] duration-200 ease-in-out"
                style={{ maxHeight: open ? panelHeight || undefined : 0 }}
            >
                <div ref={contentRef}>
                    {/*
                     * `inert` while collapsed. The actions stay mounted so the height can animate
                     * both ways, and inert is what keeps a zero-height panel out of the tab order
                     * and off a screen reader — hiding it with height alone leaves both reachable.
                     */}
                    <div id={actionsId} inert={!open} className="flex flex-col gap-0.5 p-1">
                        <button
                            type="button"
                            className={actionClass}
                            onClick={() => closeAndGo(() => navigate(profilePath))}
                        >
                            <User className="h-4 w-4 shrink-0 group-data-[collapsible=icon]:mr-0" />
                            <span className="ml-2 group-data-[collapsible=icon]:hidden">{t("nav.profile")}</span>
                        </button>
                        <button
                            type="button"
                            className={actionClass}
                            onClick={() =>
                                closeAndGo(() =>
                                    logout({ logoutParams: { returnTo: window.location.origin + "/login" } }),
                                )
                            }
                        >
                            <LogOut className="h-4 w-4 shrink-0 group-data-[collapsible=icon]:mr-0" />
                            <span className="ml-2 group-data-[collapsible=icon]:hidden">{t("nav.logout")}</span>
                        </button>
                    </div>
                    {/* Divides the actions from the account row without breaking the surface. */}
                    <div className="bg-border mx-2 h-px" />
                </div>
            </div>

            <button
                ref={triggerRef}
                type="button"
                aria-expanded={open}
                aria-controls={actionsId}
                onClick={() => setOpen((wasOpen) => !wasOpen)}
                className="hover:bg-sidebar-accent focus-visible:ring-sidebar-ring flex w-full min-w-0 cursor-pointer items-center justify-between p-2 transition-colors group-data-[collapsible=icon]:justify-center focus-visible:ring-2 focus-visible:outline-none"
            >
                <span className="flex min-w-0 items-center gap-3">
                    {loading ? (
                        <>
                            <Avatar size="sm" className="shrink-0">
                                <AvatarGradientFallback></AvatarGradientFallback>
                            </Avatar>
                            <span className="flex min-w-0 flex-col pt-2 group-data-[collapsible=icon]:hidden">
                                <span className="bg-border mb-2 h-2 w-30 rounded-xl"></span>
                                <span className="bg-border mb-2 h-2 w-25 rounded-xl"></span>
                            </span>
                        </>
                    ) : (
                        <>
                            <Avatar size="sm" className="shrink-0">
                                <AvatarGradientFallback>
                                    {getInitialsFrom(user?.firstName ?? "", user?.lastName ?? "", user?.email ?? "")}
                                </AvatarGradientFallback>
                            </Avatar>
                            <span className="flex min-w-0 flex-col text-left group-data-[collapsible=icon]:hidden">
                                <Text className="text-sidebar-foreground truncate text-xs">{user?.email}</Text>
                                <Mono className="text-sidebar-muted-foreground text-xs uppercase">
                                    {user && roleLabelMap[user.role]}
                                </Mono>
                            </span>
                        </>
                    )}
                </span>
                {/*
                 * Points up when closed, because that is where the panel comes from, and turns
                 * over to point down when open. It was a static chevron-down, which pointed away
                 * from everything it was about to reveal.
                 */}
                <ChevronUp
                    aria-hidden
                    className={cn(
                        "text-sidebar-muted-foreground ml-2 h-4 w-4 shrink-0 transition-transform duration-200 ease-in-out",
                        "group-data-[collapsible=icon]:hidden",
                        open && "rotate-180",
                    )}
                />
            </button>
        </div>
    );
}
